import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { InterviewProvider, useInterview } from '@features/interview/context/InterviewContext';
import { ROUTES, INTERVIEW_PHASES, STORAGE_KEYS } from '@shared/utils/constants';

// Pages
import LoginPage from '@features/auth/pages/LoginPage';
import ProceedPage from '@features/interview/pages/ProceedPage';
import CameraCheckPage from '@features/media/pages/CameraCheckPage';
import LivePreviewPage from '@features/media/pages/LivePreviewPage';
import InterviewPage from '@features/interview/pages/InterviewPage';
import CompletedPage from '@features/interview/pages/CompletedPage';

// Route Guard Component
const ProtectedRoute = ({ children, requiredPhases = [] }) => {
  const { state } = useInterview();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Wait for auth initialization before making routing decisions
    if (!state.isAuthInitialized) {
      return;
    }

    // Check if user is authenticated for protected routes
    if (!state.isAuthenticated && location.pathname !== ROUTES.LOGIN) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    // Phase-based routing - redirect user to correct page based on their phase
    if (state.isAuthenticated && requiredPhases.length > 0 && !requiredPhases.includes(state.phase)) {
      const phaseRouteMap = {
        [INTERVIEW_PHASES.LOGIN]: ROUTES.LOGIN,
        [INTERVIEW_PHASES.PROCEED]: ROUTES.PROCEED,
        [INTERVIEW_PHASES.CAMERA_CHECK]: ROUTES.CAMERA_CHECK,
        [INTERVIEW_PHASES.LIVE_PREVIEW]: ROUTES.LIVE_PREVIEW,
        [INTERVIEW_PHASES.INTERVIEW]: ROUTES.INTERVIEW,
        [INTERVIEW_PHASES.COMPLETED]: ROUTES.COMPLETED,
      };

      const targetRoute = phaseRouteMap[state.phase];
      if (targetRoute && targetRoute !== location.pathname) {
        navigate(targetRoute, { replace: true });
      }
    }
  }, [state.isAuthInitialized, state.isAuthenticated, state.phase, navigate, location.pathname, requiredPhases]);

  // Show nothing while auth is initializing (prevents flash of login page)
  if (!state.isAuthInitialized) {
    return null;
  }

  return children;
};

// Page Transition Wrapper
const PageTransition = ({ children }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="min-h-screen"
    >
      {children}
    </motion.div>
  );
};

// Network Status Banner
const NetworkStatusBanner = () => {
  const { state } = useInterview();
  const [isOffline, setIsOffline] = React.useState(!navigator.onLine);
  const [showBanner, setShowBanner] = React.useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setShowBanner(true);
      setTimeout(() => setShowBanner(false), 3000);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showBanner && !isOffline) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className={`
          fixed top-0 left-0 right-0 z-50 py-3 px-4 text-center font-medium
          ${isOffline 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
          }
        `}
      >
        {isOffline ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
            </svg>
            No internet connection. Please check your network.
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Connection restored!
          </span>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 p-8">
          <div className="max-w-md text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">An unexpected error occurred. Please refresh the page to try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary px-8 py-3"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// App Routes Component
const AppRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Login Page */}
        <Route
          path={ROUTES.LOGIN}
          element={
            <PageTransition>
              <LoginPage />
            </PageTransition>
          }
        />

        {/* Proceed Page */}
        <Route
          path={ROUTES.PROCEED}
          element={
            <ProtectedRoute requiredPhases={[INTERVIEW_PHASES.PROCEED, INTERVIEW_PHASES.LOGIN]}>
              <PageTransition>
                <ProceedPage />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Camera Check Page */}
        <Route
          path={ROUTES.CAMERA_CHECK}
          element={
            <ProtectedRoute requiredPhases={[INTERVIEW_PHASES.CAMERA_CHECK, INTERVIEW_PHASES.PROCEED]}>
              <PageTransition>
                <CameraCheckPage />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Live Preview Page */}
        <Route
          path={ROUTES.LIVE_PREVIEW}
          element={
            <ProtectedRoute requiredPhases={[INTERVIEW_PHASES.LIVE_PREVIEW, INTERVIEW_PHASES.CAMERA_CHECK]}>
              <PageTransition>
                <LivePreviewPage />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Interview Page */}
        <Route
          path={ROUTES.INTERVIEW}
          element={
            <ProtectedRoute requiredPhases={[INTERVIEW_PHASES.INTERVIEW, INTERVIEW_PHASES.LIVE_PREVIEW]}>
              <PageTransition>
                <InterviewPage />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Completed Page */}
        <Route
          path={ROUTES.COMPLETED}
          element={
            <ProtectedRoute requiredPhases={[INTERVIEW_PHASES.COMPLETED]}>
              <PageTransition>
                <CompletedPage />
              </PageTransition>
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </AnimatePresence>
  );
};

// Main App Component
const App = () => {
  // Initialize app settings
  useEffect(() => {
    // Check for saved language preference - Default to Arabic
    const savedLang = localStorage.getItem(STORAGE_KEYS.LANGUAGE);
    if (savedLang === 'en') {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    } else {
      // Default to Arabic (if no saved preference or if saved as 'ar')
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    }

    // Prevent zoom on mobile
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }

    // Handle visibility change (tab switch)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Tab is hidden - interview may be affected');
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <ErrorBoundary>
      <InterviewProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="relative min-h-screen bg-surface-50">
            <NetworkStatusBanner />
            <AppRoutes />
          </div>
        </Router>
      </InterviewProvider>
    </ErrorBoundary>
  );
};

export default App;
